import { useEffect, useRef } from "react";
import {
  useContentShareState,
  useRemoteVideoTileState,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import type { RosterType } from "amazon-chime-sdk-component-library-react";
import { twMerge } from "tailwind-merge";
import { ErrorBoundary } from "@themes/pollinator/ui/ErrorBoundary";
import { Loader } from "@themes/pollinator/ui/Loader";
import { useUserFullname } from "@platform/hooks/useUserFullname";
import { VideoMeetingGrid } from "./VideoMeetingGrid";
import { VideoMeetingTileLocalVideoContainer } from "../VideoMeetingTile/VideoMeetingTileLocalVideoContainer";
import { VideoMeetingTileRemoteVideoContainer } from "../VideoMeetingTile/VideoMeetingTileRemoteVideoContainer";
import { extractRoomMemberData } from "@core/features/video";
import { usePlatformStore, useVideoRoster, useVideoMeetingMembers } from "@platform/store";
import { useVideoGridSize } from "../../hooks/useVideoGridSize";

export function VideoMeetingGridContainer() {
  const userFullname = useUserFullname();

  const { roomMembersData } = useVideoMeetingMembers();

  const { gridSize } = useVideoGridSize();

  const meetingManager = useMeetingManager();

  const roster = useVideoRoster();

  // Cache roster entries so departed attendees keep their name during grace
  const lastRosterRef = useRef<RosterType>({});
  useEffect(() => {
    lastRosterRef.current = { ...lastRosterRef.current, ...roster };
  }, [roster]);

  const { tileIdToAttendeeId } = useRemoteVideoTileState();

  const { tiles: contentShareTiles } = useContentShareState();

  const hasContentShare = contentShareTiles && contentShareTiles.length > 0;

  // Get local attendee ID to exclude it from remote tiles
  const localAttendeeId =
    meetingManager.meetingSession?.configuration.credentials?.attendeeId;

  // Create a map of attendee IDs to tile IDs for attendees with video
  const attendeeIdToTileId = new Map<string, number>();
  Object.entries(tileIdToAttendeeId).forEach(([tileId, attendeeId]) => {
    attendeeIdToTileId.set(attendeeId, parseInt(tileId));
  });

  // Tile state from the unified observer — attendees with 'absent' are not rendered
  const attendeeTileState = usePlatformStore((s) => s.attendeeTileState);

  // Get all remote attendees (excluding local and absent)
  const remoteAttendees = Object.entries(roster).filter(
    ([attendeeId]) =>
      attendeeId !== localAttendeeId &&
      attendeeTileState[attendeeId] !== 'absent'
  );

  // Also include attendees who are in attendeeTileState but not yet in roster
  // (source arrived before roster — handled by useChimeObserver)
  const rosterIds = new Set(remoteAttendees.map(([id]) => id));
  const tileOnlyAttendees = Object.entries(attendeeTileState)
    .filter(
      ([id, state]) =>
        !rosterIds.has(id) &&
        id !== localAttendeeId &&
        state !== 'absent'
    )
    .map(([id]) => [id, lastRosterRef.current[id] ?? roster[id] ?? { name: "" }] as const);

  const allAttendees = [
    ...remoteAttendees,
    ...tileOnlyAttendees.map(([id, attendee]) => [id, attendee] as [string, typeof attendee]),
  ];

  // Deduplicate by user ID — after a page refresh, the old attendee lingers
  // in the grace period while the new one is live.
  const deduped = (() => {
    const liveIds = new Set(Object.keys(roster));
    const byUserId = new Map<string, (typeof allAttendees)[number][]>();

    for (const entry of allAttendees) {
      const [, attendee] = entry;
      const extId = attendee?.externalUserId ?? "";
      const uidIdx = extId.lastIndexOf("-UID-");
      const userId = uidIdx !== -1 ? extId.slice(uidIdx + 5) : null;

      if (!userId) {
        byUserId.set(entry[0], [entry]);
        continue;
      }

      const group = byUserId.get(userId) ?? [];
      group.push(entry);
      byUserId.set(userId, group);
    }

    const result: (typeof allAttendees)[number][] = [];
    for (const entries of byUserId.values()) {
      if (entries.length === 1) {
        result.push(entries[0]);
        continue;
      }

      const live = entries.filter(([id]) => liveIds.has(id));
      const graceOnly = entries.filter(([id]) => !liveIds.has(id));

      if (live.length > 0) {
        result.push(...live);
      } else {
        const withTile = graceOnly.find(([id]) => attendeeIdToTileId.has(id));
        result.push(withTile ?? graceOnly[0]);
      }
    }
    return result;
  })();

  const errorResetKey = `${gridSize}-${hasContentShare}`;

  return (
    <ErrorBoundary
      fallback={<Loader isAbsolute />}
      resetKey={errorResetKey}
      className={twMerge("flex w-full", !hasContentShare && "h-full")}
    >
      <VideoMeetingGrid size={gridSize} isContentShare={hasContentShare}>
        {deduped.map(([attendeeId, attendee]) => {
          const tileId = attendeeIdToTileId.get(attendeeId);
          const { displayName, roomMemberData } = extractRoomMemberData(
            attendee,
            roomMembersData,
          );

          return (
            <VideoMeetingTileRemoteVideoContainer
              isWithContentShare={hasContentShare}
              key={attendeeId}
              tileId={tileId || 0}
              attendeeId={attendeeId}
              name={displayName}
              roomMemberData={roomMemberData}
            />
          );
        })}
        <VideoMeetingTileLocalVideoContainer
          totalTiles={gridSize}
          nameplate={userFullname}
          isWithContentShare={hasContentShare}
        />
      </VideoMeetingGrid>
    </ErrorBoundary>
  );
}

export default VideoMeetingGridContainer;
